'use client'

import { useContext, useEffect, ChangeEvent } from 'react'
import { useIntl } from 'react-intl'
import { Button, Cell, Dialog, Divider, InfoBanner, Navigation, Radio, Text } from '@vinted/web-ui'
import { ArrowLeft24, X24 } from '@vinted/monochrome-icons'

import { GetShippingLabelContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { List } from '@marketplace-web/common-components/list-ui'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  GenerateLabelModal,
  DropOffTypeKey,
  CollectionDateModel,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

import { getDropOffHour } from '../utils'

type Props = {
  isOpen: boolean
}

const CourierPickUpDateSelectionModal = ({ isOpen }: Props) => {
  const translate = useTranslate('conversation.pick_up_date.modal')
  const errorTranslate = useTranslate('conversation.generic_error_modal')
  const { formatDate } = useIntl()
  const { conversation } = useConversationContext()

  const shipmentId = conversation?.transaction?.shipment?.id

  const {
    state: {
      collectionDates: {
        collectionDates,
        selectedCollectionDate,
        selectedCollectionTime,
        availableCollectionDates,
        availableCollectionTimes,
        uiState,
        fetchCollectionDates,
        selectCollectionDate: setSelectedCollectionDate,
        selectCollectionTime: setSelectedCollectionTime,
        clearSelectedCollection,
        confirmCollectionDateSelection,
        collectionDatesError,
        clearCollectionDatesError,
      },
      activeModal: { setActiveModal },
    },
  } = useContext(GetShippingLabelContext)

  const isContactlessDropoffFlowEnabled =
    collectionDates?.dropOffType === DropOffTypeKey.ContactlessDropoff

  useEffect(() => {
    if (!isOpen || !shipmentId) return

    fetchCollectionDates({ shipmentId })
  }, [fetchCollectionDates, isOpen, shipmentId])

  function clearSelection() {
    if (collectionDates) clearSelectedCollection()
    if (collectionDatesError) clearCollectionDatesError()
  }

  function handleClose() {
    clearSelection()
    setActiveModal(GenerateLabelModal.None)
  }

  function handlePrevious() {
    clearSelection()
    setActiveModal(GenerateLabelModal.GetShippingLabelModal)
  }

  function handleConfirm() {
    if (!selectedCollectionDate || !selectedCollectionTime) return
    confirmCollectionDateSelection(selectedCollectionDate, selectedCollectionTime)
    setActiveModal(GenerateLabelModal.GetShippingLabelModal)
  }

  const selectCollectionDate = (collectionDate: CollectionDateModel) => () => {
    setSelectedCollectionDate(collectionDate)
  }

  const handleCollectionDateRadioSelect =
    (collectionDate: CollectionDateModel) => (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault()
      setSelectedCollectionDate(collectionDate)
    }

  const selectCollectionTime = (availableTime: CollectionDateModel) => () => {
    setSelectedCollectionTime(availableTime)
  }

  const handleCollectionTimeRadioSelect =
    (availableTime: CollectionDateModel) => (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault()
      setSelectedCollectionTime(availableTime)
    }

  function renderNavigation() {
    const modalTitle = isContactlessDropoffFlowEnabled ? 'drop_off_title' : 'title'

    return (
      <>
        <Navigation
          body={translate(modalTitle)}
          right={
            <Button
              inline
              iconName={X24}
              styling="flat"
              testId="pick-up-date-modal-navigation-close-button"
              onClick={handleClose}
            />
          }
          left={
            <Button
              styling="flat"
              iconName={ArrowLeft24}
              inline
              testId="pick-up-date-modal-previous"
              onClick={handlePrevious}
            />
          }
        />
        <Divider />
      </>
    )
  }

  function renderTitle() {
    const titleText = isContactlessDropoffFlowEnabled ? 'drop_off_date.title' : 'pick_up_date.title'

    return <Cell title={<Text as="h1" type="heading" text={translate(titleText)} />} />
  }

  function renderBanner() {
    if (!isContactlessDropoffFlowEnabled) return null

    const dropOffTime = collectionDates.dates[0]!.dropOffTo

    if (!dropOffTime) return null

    return (
      <InfoBanner
        title={translate('info_banner_title')}
        body={translate('info_banner_body', {
          time: getDropOffHour(dropOffTime),
        })}
        type="warning"
        testId="collection-date-info-banner"
      />
    )
  }

  const formatCollectionDay = (dayTimestamp?: string | null) => {
    if (!dayTimestamp) return ''

    // Need to remove +02:00 from the timestamp to correctly format the date
    const isoDate = dayTimestamp.split('T')[0]

    const date = new Date(`${isoDate!}T00:00:00Z`)

    const formattedDate = formatDate(date, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    })

    return formattedDate
  }

  const formatCollectionTime = (timeTimestamp?: string | null) => {
    if (!timeTimestamp) return ''

    // Need to remove +02:00 from the timestamp to correctly format the time
    const timeMatch = timeTimestamp.match(/T(\d{2}:\d{2})/)

    return timeMatch ? timeMatch[1] : ''
  }

  function renderCollectionDates(collectionDate: CollectionDateModel) {
    if (!collectionDate.collection) return null

    return (
      <Cell
        key={collectionDate.collection}
        type="navigating"
        title={formatCollectionDay(collectionDate.collection)}
        onClick={selectCollectionDate(collectionDate)}
        suffix={
          <Radio
            name="collection-date-radio"
            checked={collectionDate.collection === selectedCollectionDate?.collection}
            value={collectionDate.collection}
            onChange={handleCollectionDateRadioSelect(collectionDate)}
            aria={{
              'aria-labelledby': collectionDate.collection ?? undefined,
            }}
          />
        }
        testId="collection-date"
      />
    )
  }

  function renderDatesSelection() {
    return availableCollectionDates?.map(renderCollectionDates)
  }

  function renderNote() {
    if (!collectionDates?.shipmentDeadline) return null

    return <Cell title={<Text as="h4" type="caption" text={collectionDates.shipmentDeadline} />} />
  }

  function renderCollectionTimes() {
    if (!availableCollectionTimes) return null

    return (
      <List>
        {availableCollectionTimes.map(availableTime => {
          if (!availableTime.collection || !availableTime.collectionTo) return null

          return (
            <Cell
              key={availableTime.collectionTo}
              type="navigating"
              title={`${formatCollectionTime(availableTime.collection)} - ${formatCollectionTime(availableTime.collectionTo)}`}
              onClick={selectCollectionTime(availableTime)}
              suffix={
                <Radio
                  name="collection-date-time-radio"
                  checked={availableTime === selectedCollectionTime}
                  value={availableTime.collection}
                  onChange={handleCollectionTimeRadioSelect(availableTime)}
                  aria={{
                    'aria-labelledby': availableTime.collection ?? undefined,
                  }}
                />
              }
            />
          )
        })}
      </List>
    )
  }

  function renderTimeSelectionNote() {
    if (isContactlessDropoffFlowEnabled) return null

    return <Cell title={<Text as="h1" type="heading" text={translate('pick_up_time.title')} />} />
  }

  function renderSubmit() {
    return (
      <Cell>
        <Button
          styling="filled"
          text={translate('action.confirm')}
          onClick={handleConfirm}
          testId="pick-up-date-modal-confirm-button"
        />
      </Cell>
    )
  }

  function renderEmptyCollectionDates() {
    return (
      <ScrollableArea>
        {renderNavigation()}
        {renderTitle()}
        <InfoBanner
          body={translate('empty_state')}
          type="error"
          testId="collection-date-error-banner"
        />
        {renderNote()}
        <Cell>
          <Button
            styling="filled"
            text={translate('action.confirm')}
            disabled
            testId="pick-up-date-modal-disabled-button"
          />
        </Cell>
      </ScrollableArea>
    )
  }

  if (collectionDatesError) {
    return (
      <Dialog
        show
        title={errorTranslate('title')}
        body={<Text as="span" text={collectionDatesError} alignment="center" />}
        actions={[
          {
            text: errorTranslate('actions.close'),
            style: Dialog.ActionStyling.Filled,
            callback: handlePrevious,
            testId: 'generic-error-modal-action-button',
          },
        ]}
        testId="generic-error-modal"
      />
    )
  }

  function renderContent() {
    if (uiState === UiState.Pending) return <ContentLoader testId="collection-dates-loader" />

    return (
      <div className="u-fill-width">
        <ScrollableArea>
          {renderNavigation()}
          {renderBanner()}
          {renderTitle()}
          <List>{renderDatesSelection()}</List>
          <Divider />
          {renderNote()}
          {renderTimeSelectionNote()}
          {renderCollectionTimes()}
          {renderSubmit()}
        </ScrollableArea>
      </div>
    )
  }

  return (
    <Dialog show={isOpen} hasScrollableContent>
      {collectionDates?.dates.length === 0 ? renderEmptyCollectionDates() : renderContent()}
    </Dialog>
  )
}

export default CourierPickUpDateSelectionModal
